<template>
  <v-autocomplete
    v-if="!allowCustomValues"
    ref="autocomplete"
    v-model="selectedValue"
    :search-input.sync="searchText"
    :items="items"
    :label="label"
    :rules="computedRules"
    :clearable="isClearable"
    :clear-icon="icons.mdiClose"
    :disabled="disabled"
    :readonly="readonly"
    :item-value="computedValue"
    :item-text="computedText"
    :prepend-icon="prependIcon"
    :append-icon="appendIcon"
    :multiple="multiple"
    :return-object="returnObject"
    :dense="dense"
    :hint="hint"
    :hide-details="hideDetails"
    :single-line="singleLine"
    :class="[noteRequiredField ? 'required-field' : '', noOutline ? 'no-outline' : '', noTruncate ? 'no-truncate-selected' : '']"
    :menu-props="menuProps"
    :attach="!notAttached"
    :outlined="!noOutline"
    :chips="false"
    @change="valuesChanged($event)"
    @update:search-input="onSearchInputUpdate"
    @click:prepend="$emit('click:prepend', $event)"
    @click:append="$emit('click:append', $event)"
    @click:clear="$emit('click:clear', $event)"
    @click="$emit('click', $event)"
  >
    <template v-if="noteRequiredField" #label>
      <strong class="error--text">*</strong>
      {{ label }}
    </template>
    <template #prepend-inner>
      <input-history
        :title="label"
        :value="previousValue"
        :dos="previousValueDos"
        :custom-styles="inputHistoryCustomStyles"
        prepended
      />
    </template>
    <template #append>
      <v-icon v-if="appendIcon">
        {{ appendIcon }}
      </v-icon>
    </template>
  </v-autocomplete>


  <v-combobox
    v-else
    ref="autocomplete"
    v-model="selectedValue"
    :maxlength="maxLength"
    :counter="counter"
    :search-input.sync="searchText"
    :items="items"
    :label="label"
    :rules="computedRules"
    :clearable="isClearable"
    :clear-icon="icons.mdiClose"
    :disabled="disabled"
    :readonly="readonly"
    :item-value="computedValue"
    :item-text="computedText"
    :prepend-icon="prependIcon"
    :append-icon="appendIcon"
    :multiple="multiple"
    :return-object="returnObject"
    :dense="dense"
    :hint="hint"
    :hide-details="hideDetails"
    :single-line="singleLine"
    :class="[noteRequiredField ? 'required-field' : '', noOutline ? 'no-outline' : '', noTruncate ? 'no-truncate-selected' : '']"
    :menu-props="menuProps"
    :attach="!notAttached"
    :outlined="!noOutline"
    :chips="false"
    @change="valuesChanged($event)"
    @update:search-input="onSearchInputUpdate"
    @click:prepend="$emit('click:prepend', $event)"
    @click:append="$emit('click:append', $event)"
    @click:clear="$emit('click:clear', $event)"
    @click="$emit('click', $event)"
  >
    <template v-if="noteRequiredField" #label>
      <strong class="error--text">*</strong>
      {{ label }}
    </template>
    <template #prepend-inner>
      <input-history
        :title="label"
        :value="previousValue"
        :dos="previousValueDos"
        :custom-styles="inputHistoryCustomStyles"
        prepended
      />
    </template>
    <template #append>
      <v-icon v-if="appendIcon">
        {{ appendIcon }}
      </v-icon>
    </template>
  </v-combobox>
</template>

<script>
import { mdiClose, mdiMenuDown } from '@mdi/js';
import InputHistory from './InputHistory.vue';

export default {
  name: 'AutoCompleteBox',
  components: { InputHistory },
  props: {
    value: { type: [String, Number, Boolean, Array, Object], default: null },
    items: { type: Array, default: null },
    menuProps: { type: Object, default: () => ({ offsetY: true, closeOnClick: true }) },
    label: { type: String, default: '' },
    required: { type: Boolean, default: null },
    clearable: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    itemValue: { type: String, default: null },
    itemText: { type: String, default: null },
    rules: { type: String, default: null },
    prependIcon: { type: String, default: null },
    appendIcon: { type: String, default: mdiMenuDown },
    multiple: { type: Boolean, default: false },
    dense: { type: Boolean, default: false },
    hideDetails: { type: Boolean, default: false },
    returnObject: { type: Boolean, default: false },
    hint: { type: String, default: null },
    singleLine: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    notAttached: { type: Boolean, default: false },
    noOutline: { type: Boolean, default: false },
    previousValue: { type: [String, Boolean, Number], default: null },
    previousValueDos: { type: String, default: null },
    inputHistoryCustomStyles: { type: String, default: null },
    noTruncate: { type: Boolean, default: false },
    allowCustomValues: { type: Boolean, default: false },
    maxLength: { type: Number, default: 255 },
    counter: { type: Boolean, default: false },
    distinctiveNone: { type: Boolean, default: false, },
  },
  data() {
    return {
      selectedValue: this.value,
      icons: { mdiMenuDown, mdiClose },
      searchText: '',
    }
  },
  computed: {
    noteRequiredField() {
      return this.required && (!this.selectedValue || (Array.isArray(this.selectedValue) && !this.selectedValue.length))
    },
    isClearable() {
      return this.clearable !== 'false'
    },
    computedRules() {
      let rulesArray = []
      if (this.disabled || this.readonly) return rulesArray
      if (this.rules && this.$validationRules) {
        rulesArray = rulesArray.concat(eval(`this.$validationRules.${this.rules}`))
      }
      if (this.required && this.$validationRules) {
        rulesArray = this.multiple
          ? rulesArray.concat(this.$validationRules.requiredCheckboxes)
          : rulesArray.concat(this.$validationRules.required)
      }
      return rulesArray
    },
    computedValue() {
      if (this.itemValue) return this.itemValue
      if (this.items && this.items.length) {
        if (typeof this.items[0].id !== 'undefined') return 'id'
        if (typeof this.items[0].value !== 'undefined') return 'value'
        if (typeof this.items[0].title !== 'undefined') return 'title'
        if (typeof this.items[0].label !== 'undefined') return 'label'
      }
      return 'value'
    },
    computedText() {
      if (this.itemText) return this.itemText
      if (this.items && this.items.length) {
        if (typeof this.items[0].title !== 'undefined') return 'title'
      }
      return 'label'
    },
  },
  watch: {
    value(val) {
      this.selectedValue = val
    },
  },
  methods: {
    valuesChanged(event) {
      if (!this.distinctiveNone) {
        this.emitChanges(event)
        return
      }

      const NONE_TITLE = 'None'
      const lastSelected = event[event.length - 1] // last action
      const hasNone = event.some(x => x.title === NONE_TITLE)

      let newValue = event
      let notifyMessage = ''

      if (hasNone) {
        if (lastSelected.title === NONE_TITLE) {
          // User just selected "None" → keep only None, remove others
          if (event.length > 1) {
            notifyMessage = '"None" is exclusive and other values were removed.'
          }
          newValue = event.filter(x => x.title === NONE_TITLE)
        } else {
          // User selected other values while None exists → remove None
          notifyMessage = '"None" was removed because other values were selected.'
          newValue = event.filter(x => x.title !== NONE_TITLE)
        }
      }

      this.selectedValue = newValue
      this.emitChanges(newValue)

      if (notifyMessage) {
        this.$store.dispatch('notify', {
          value: notifyMessage,
          color: 'warning'
        })
      }
    },
    emitChanges(event) {
      this.$emit('change', event)
      this.$emit('input', event)
      // Clear the search input after selection to prevent keyword from being appended
      this.searchText = ''
    },
    onSearchInputUpdate(val) {
      this.searchText = val
    },
  },
}
</script>

<style lang="scss">
.v-autocomplete, .v-menu__content {
  .v-select-list > .v-subheader {
    display: grid;
    font-size: 1rem;
    line-height: 1rem;
    text-align: center;
    color: var(--v-secondary-lighten5);
    background-color: var(--v-secondary-lighten1);
  }
}

.no-truncate-selected .v-select__selection {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: unset !important;
  word-break: break-word;
  line-height: 1.2 !important
}
</style>
